"use client";

import { createTranslator } from "next-intl";
import Link from "next/link";
import type React from "react";
import { useLocale, useTranslations } from "next-intl";

type ValuesMap = Record<string, string | number>;
type RichValuesMap = Record<
  string,
  string | number | ((chunks: React.ReactNode) => React.ReactNode)
>;

const SAFE_HREF_SCHEMES = new Set(["http", "https", "mailto", "tel"]);

function sanitizeHref(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return "#";
  if (trimmed.startsWith("//")) return "#";

  const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!schemeMatch) {
    return trimmed;
  }

  return SAFE_HREF_SCHEMES.has(schemeMatch[1].toLowerCase()) ? trimmed : "#";
}

function normalizeAnchorTags(template: string): {
  normalizedTemplate: string;
  hrefByTag: Record<string, string>;
} {
  const openTags = template.match(/<a\b[^>]*>/gi) ?? [];
  const closeTags = template.match(/<\/a>/gi) ?? [];
  if (openTags.length !== closeTags.length) {
    return { normalizedTemplate: template, hrefByTag: {} };
  }

  const hrefByTag: Record<string, string> = {};
  let openIndex = 0;
  let closeIndex = 0;

  const withNormalizedOpenTags = template.replace(
    /<a\b([^>]*)>/gi,
    (_, rawAttributes) => {
      const tagName = `link${openIndex}`;
      const hrefMatch = String(rawAttributes).match(
        /\bhref\s*=\s*(['"])(.*?)\1/i,
      );
      hrefByTag[tagName] = sanitizeHref(hrefMatch?.[2] ?? "#");
      openIndex += 1;
      return `<${tagName}>`;
    },
  );

  const normalizedTemplate = withNormalizedOpenTags.replace(/<\/a>/gi, () => {
    const tagName = `link${closeIndex}`;
    closeIndex += 1;
    return `</${tagName}>`;
  });

  return { normalizedTemplate, hrefByTag };
}

export function Trans(props: {
  i18nKey: string;
  values?: ValuesMap;
  fallback?: string;
  linkClassName?: string;
  href?: string;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const sanitizedHref = props.href ? sanitizeHref(props.href) : undefined;
  const linkRenderer = (chunks: React.ReactNode) => {
    if (!sanitizedHref) {
      return <>{chunks}</>;
    }
    return (
      <Link href={sanitizedHref} className={props.linkClassName}>
        {chunks}
      </Link>
    );
  };

  const richTranslate = (
    t as {
      rich?: (key: string, values?: RichValuesMap) => React.ReactNode;
    }
  ).rich;

  if (richTranslate) {
    try {
      return (
        <>
          {richTranslate(props.i18nKey, {
            ...(props.values ?? {}),
            link: linkRenderer,
          })}
        </>
      );
    } catch {
      // Fall back to legacy anchor-normalization mode below.
    }
  }

  const resolvedTemplate = t(props.i18nKey, props.values);
  const template =
    props.fallback && resolvedTemplate === props.i18nKey
      ? props.fallback
      : resolvedTemplate;
  const { normalizedTemplate, hrefByTag } = normalizeAnchorTags(template);

  const translator = createTranslator({
    locale,
    messages: {
      __trans: normalizedTemplate,
    },
  });

  const tags: Record<string, (chunks: React.ReactNode) => React.ReactNode> = {};

  for (const [tagName, href] of Object.entries(hrefByTag)) {
    tags[tagName] = (chunks) => (
      <Link href={href} className={props.linkClassName}>
        {chunks}
      </Link>
    );
  }

  return <>{translator.rich("__trans", tags)}</>;
}
